import React, { useEffect, Fragment } from 'react';
// src/components/ui/Modal.tsx

import { Dialog, Transition } from '@headlessui/react';
import { XIcon } from 'lucide-react';
interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: React.ReactNode;
  description?: React.ReactNode;
  children: React.ReactNode;
  footer?: React.ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | '2xl' | 'full';
  closeOnClickOutside?: boolean;
  showCloseButton?: boolean;
  contentClassName?: string;
  initialFocus?: React.RefObject<HTMLElement>;
}
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  description,
  children,
  footer,
  size = 'md',
  closeOnClickOutside = true,
  showCloseButton = true,
  contentClassName = '',
  initialFocus
}) => {
  // Prevent body scrolling when modal is open
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('overflow-hidden');
    } else {
      document.body.classList.remove('overflow-hidden');
    }
    return () => {
      document.body.classList.remove('overflow-hidden');
    };
  }, [isOpen]);
  // Get max width class based on size prop
  const getMaxWidthClass = () => {
    switch (size) {
      case 'sm':
        return 'sm:max-w-sm';
      case 'md':
        return 'sm:max-w-md';
      case 'lg':
        return 'sm:max-w-lg';
      case 'xl':
        return 'sm:max-w-xl';
      case '2xl':
        return 'sm:max-w-2xl';
      case 'full':
        return 'sm:max-w-[90%] sm:h-[90%]';
      default:
        return 'sm:max-w-md';
    }
  };
  return <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="fixed z-50 inset-0 overflow-y-auto" onClose={closeOnClickOutside ? onClose : () => {}} initialFocus={initialFocus}>
        <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0" enterTo="opacity-100" leave="ease-in duration-200" leaveFrom="opacity-100" leaveTo="opacity-0">
            <div className="fixed inset-0 bg-navy-950/45 transition-opacity" />
          </Transition.Child>
          {/* This element is to trick the browser into centering the modal contents. */}
          <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">
            &#8203;
          </span>
          <Transition.Child as={Fragment} enter="ease-out duration-300" enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95" enterTo="opacity-100 translate-y-0 sm:scale-100" leave="ease-in duration-200" leaveFrom="opacity-100 translate-y-0 sm:scale-100" leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">
            <div className={`inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-e3 transform transition-all sm:my-8 sm:align-middle ${getMaxWidthClass()} w-full ${size === 'full' ? 'max-h-full' : ''}`}>
              {/* Close button */}
              {showCloseButton && <div className="absolute top-0 right-0 pt-4 pr-4">
                  <button type="button" className="bg-white rounded-md text-ink-400 hover:text-ink-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-navy-600/55" onClick={onClose}>
                    <span className="sr-only">Close</span>
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>}
              {/* Header */}
              {(title || description) && <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="sm:flex sm:items-start">
                    <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                      {title && <Dialog.Title as="h3" className="text-section text-ink-900">
                          {title}
                        </Dialog.Title>}
                      {description && <div className="mt-2">
                          <p className="text-body-sm text-ink-500">{description}</p>
                        </div>}
                    </div>
                  </div>
                </div>}
              {/* Content */}
              <div className={`bg-white px-4 pt-5 pb-4 sm:p-6 ${title || description ? 'border-t border-ink-200' : ''} ${contentClassName} ${size === 'full' ? 'overflow-y-auto max-h-[calc(90vh-200px)]' : ''}`}>
                {children}
              </div>
              {/* Footer */}
              {footer && <div className="bg-ink-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse border-t border-ink-200">
                  {footer}
                </div>}
            </div>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>;
};
export default Modal;